<template>
  <component-wrapper
    :key="catalogId || 'loading'"
    class="product-detail-page-fi product-detail-page-fite-fi"
  >
    <product-detail-head-fite-fi
      :catalog-data="getCatalogProductDataById"
      :media-download-link="mediaDownloadLink"
      :fields="fields"
      :no-margin-bottom="Boolean(accessories?.length)"
    />

    <product-items-slider-fi
      v-if="accessories?.length"
      :products="accessories"
      :slider-headline="$dict.get('product-Family.Accessories.Headline-Fite')"
      white
      expandable
    />

    <product-detail-accordions-fi
      v-if="getCatalogProductDataById"
      :catalog-data="getCatalogProductDataById"
    />

    <section-divider-small-fi />

    <product-items-slider-fi
      v-if="mandatoryAccessories?.length"
      :products="mandatoryAccessories"
      :slider-headline="$dict.get('product-Family.Accessories-Mandatory.Headline')"
      is-light
    />

    <product-detail-schema-fi
      v-if="getCatalogProductDataById"
      :catalog-data="getCatalogProductDataById"
    />
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { computed, onServerPrefetch, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import ProductDetailAccordionsFi from '@/components/product-detail/product-detail-accordions-fi/ProductDetailAccordionsFi.vue';
import ProductDetailHeadFiteFi from '@/components/product-detail/product-detail-head-fi/ProductDetailHeadFiteFi.vue';
import ProductDetailSchemaFi from '@/components/product-detail/product-detail-schema-fi/ProductDetailSchemaFi.vue';
import ProductItemsSliderFi from '@/components/product-items-slider-fi/ProductItemsSliderFi.vue';
import { useCatalog } from '@/composables/catalog';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type {
  GetCatalogProductDataByIdQuery,
  GetCatalogProductDataByIdQueryVariables,
  ProductsQuery,
  ProductsQueryVariables,
} from '@/generated/graphql';
import GET_CATALOG_PRODUCT_DATA_BY_ID from '@/graphql/getCatalogProductDataById.gql';
import GET_SHOPIFY_PRODUCTS from '@/graphql/getShopifyProducts.gql';
import SectionDividerSmallFi from '@/pages/section-divider-fi/SectionDividerSmallFi.vue';
import { useShopifyStore } from '@/store/pinia/shopify';

defineOptions({ name: 'ProductDetailPageFiteFi' });

const fields = useFields('ProductDetailPageFiteFiFields');

const fischer = useFischer();
const { mediaDownloadLink, checkForMediaDownload } = useCatalog();
const { isPublicAssortment, getSalesChannels } = useSalesChannelFilter();
const shopifyStore = useShopifyStore();

const catalogId = computed(() => fischer.catalogId('559879'));

// R1: catalog product query — SEO-critical content, needs R0
const { data: catalogData, executeQuery: executeCatalogQuery } = useQuery<
  GetCatalogProductDataByIdQuery,
  GetCatalogProductDataByIdQueryVariables
>({
  query: GET_CATALOG_PRODUCT_DATA_BY_ID,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    apiLang: fischer.catalogLocale(),
    getMandatoryAccessories: true,
    publicAssortment: isPublicAssortment(),
    salesChannels: getSalesChannels(),
    // NOTE: already gated in the pause condition
    id: catalogId.value ?? '',
  })),
  pause: computed(() => !catalogId.value),
});

// R0: product data is SEO-critical, must be available on first SSR paint
onServerPrefetch(async () => {
  if (!catalogId.value) return;
  await executeCatalogQuery();
});

const getCatalogProductDataById = computed(
  () => catalogData.value?.getCatalogProductDataById ?? null,
);
const accessories = computed(() =>
  getCatalogProductDataById.value?.accessories?.filter((item) => item !== null),
);
const mandatoryAccessories = computed(() =>
  getCatalogProductDataById.value?.mandatoryAccessories?.filter((item) => item !== null),
);

const hasShopifyProducts = computed(() =>
  getCatalogProductDataById.value?.Attributes?.some(
    (attribute: any) => attribute.AttributeKey === 'ShopifyID',
  ),
);

// R1: Shopify query — gated on isFite + fields.enableShopify + hasShopifyProducts (computed from
// catalog data); this depends on client-side field values and catalog result, so skip R0
const { data: shopifyData, fetching: shopifyLoading } = useQuery<
  ProductsQuery,
  ProductsQueryVariables
>({
  query: GET_SHOPIFY_PRODUCTS,
  variables: computed(() => ({ first: 100 })),
  pause: computed(
    () => !(fischer.app.isFite() && fields.enableShopify?.value && hasShopifyProducts.value),
  ),
});

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: if this result ever comes back restored from the SSR payload it is delivered during
// setup, and a lazy watcher would never see it (see createSsrExchange). No-ops when undefined.
watch(
  shopifyData,
  (data) => {
    if (data?.getShopifyProducts?.products?.edges) {
      shopifyStore.shopifyProducts = data.getShopifyProducts.products.edges;
    }
  },
  { immediate: true, flush: 'sync' },
);

watch(shopifyLoading, (loading) => {
  shopifyStore.loading = loading;
});

// created logic
if (catalogId.value) {
  checkForMediaDownload({ type: 'product', id: catalogId.value });
}
</script>
